// This hook loads the shared product module only when the caller needs it.

// Use React tools we need for the module helpers.
import { useCallback, useContext, useMemo } from "react";

// Reuse the shared script loader so the product module follows the same external module pattern.
import { useExternalScript } from "./useExternalScript";

// Import the stored app config getter.
import { GetAppConfig } from "global/config/AppConfigStore";
import { ThemeContext } from "global/context/ThemeContext";

// Reuse one cache-busting token for the whole page load so the script is refreshed once without loading multiple copies.
const PRODUCT_MODULE_CACHE_BUST = Date.now();

// Match the portal theme primary colour options so product module settings can reuse the same resolved colour value.
const PRIMARY_COLOUR_MAP = {
    purple: "#6a0dad",
    blue: "#007bff",
    pink: "#ff1493",
    orange: "#ff7f00",
    green: "#28a745",
};

// Match the portal light and dark theme tokens so product module settings can inherit the same overall look and feel.
const THEME_COLOUR_MAP = {
    light: {
        style_primary_content_colour: "#ffffff",
        style_secondary_colour: "#d9d9d9",
        style_secondary_content_colour: "#1e1e2f",
        style_base_100_colour: "#faf7f5",
        style_base_100_content_colour: "#1e1e2f",
        style_base_200_colour: "#ffffff",
        style_base_200_content_colour: "#1e1e2f",
        style_base_300_colour: "#eeeeee",
        style_base_300_content_colour: "#1e1e2f",
    },
    dark: {
        style_primary_content_colour: "#ffffff",
        style_secondary_colour: "#12121e",
        style_secondary_content_colour: "#ffffff",
        style_base_100_colour: "#1e1e2f",
        style_base_100_content_colour: "#ffffff",
        style_base_200_colour: "#2e3147",
        style_base_200_content_colour: "#ffffff",
        style_base_300_colour: "#1e2137",
        style_base_300_content_colour: "#ffffff",
    },
};

// Map product module theme settings onto the same CSS variables used by the shared modules.
const PRODUCT_THEME_VARIABLE_MAP = {
    style_primary_colour: "--strich-colour-primary",
    style_primary_content_colour: "--strich-colour-primary-content",
    style_secondary_colour: "--strich-colour-secondary",
    style_secondary_content_colour: "--strich-colour-secondary-content",
    style_accent_colour: "--strich-colour-accent",
    style_accent_content_colour: "--strich-colour-accent-content",
    style_base_100_colour: "--strich-colour-base-100",
    style_base_100_content_colour: "--strich-colour-base-100-content",
    style_base_200_colour: "--strich-colour-base-200",
    style_base_200_content_colour: "--strich-colour-base-200-content",
    style_base_300_colour: "--strich-colour-base-300",
    style_base_300_content_colour: "--strich-colour-base-300-content",
};

// Give the app one simple way to use the shared product module.
export function useProductModule({
    enabled = false,
} = {}) {

    // Read the loaded runtime config only when this hook is called.
    const appConfig = GetAppConfig();

    // Read the active portal theme when available so product styles can match the live portal theme.
    const { theme = "light", primary = "blue" } = useContext(ThemeContext) || {};

    // Read the product module script URL from runtime config.
    const productModuleUrl =
        appConfig?.productModuleUrl ||
        appConfig?.product_module_url ||
        "";

    // Add one stable cache buster so product module loads do not get stuck on an older browser-cached script.
    const cacheBustedProductModuleUrl = useMemo(() => {

        // Stop when there is no configured script URL.
        if (!productModuleUrl) {
            return "";
        }

        // Reuse one stable cache-busting token for this page load.
        const separator = productModuleUrl.includes("?") ? "&" : "?";
        return `${productModuleUrl}${separator}v=${PRODUCT_MODULE_CACHE_BUST}`;

    }, [productModuleUrl]);

    // Only load the shared script when the caller actually needs the product module.
    const status = useExternalScript(enabled ? cacheBustedProductModuleUrl : "");

    // Reuse the saved portal theme choices so the product module receives one consistent set of colours.
    const themeColours = useMemo(() => {
        const primaryColour = PRIMARY_COLOUR_MAP[String(primary || "blue").trim().toLowerCase()] || PRIMARY_COLOUR_MAP.blue;
        const baseThemeColours = THEME_COLOUR_MAP[String(theme || "light").trim().toLowerCase()] || THEME_COLOUR_MAP.light;

        return {
            ...baseThemeColours,
            style_primary_colour: primaryColour,
            style_accent_colour: primaryColour,
            style_accent_content_colour: baseThemeColours.style_primary_content_colour,
        };
    }, [primary, theme]);

    // Pass product-related cart settings directly into the shared cart module.
    const productCartSettings = useMemo(() => ([
        {
            code: "cart_basket_popup",
            value: "N",
        },
        {
            code: "style_primary_colour",
            value: themeColours.style_primary_colour,
        },
        {
            code: "style_primary_content_colour",
            value: themeColours.style_primary_content_colour,
        },
        {
            code: "style_secondary_colour",
            value: themeColours.style_secondary_colour,
        },
        {
            code: "style_secondary_content_colour",
            value: themeColours.style_secondary_content_colour,
        },
        {
            code: "style_accent_colour",
            value: themeColours.style_accent_colour,
        },
        {
            code: "style_accent_content_colour",
            value: themeColours.style_accent_content_colour,
        },
        {
            code: "style_base_100_colour",
            value: themeColours.style_base_100_colour,
        },
        {
            code: "style_base_100_content_colour",
            value: themeColours.style_base_100_content_colour,
        },
        {
            code: "style_base_200_colour",
            value: themeColours.style_base_200_colour,
        },
        {
            code: "style_base_200_content_colour",
            value: themeColours.style_base_200_content_colour,
        },
        {
            code: "style_base_300_colour",
            value: themeColours.style_base_300_colour,
        },
        {
            code: "style_base_300_content_colour",
            value: themeColours.style_base_300_content_colour,
        },
    ]), [themeColours]);

    // Read the current product API from the shared Strich namespace.
    const productModule = window.strich?.product || null;

    // Resolve a selector string or DOM node into one real mount element.
    const resolveContainerElement = useCallback((container) => {

        // Support selector strings for convenience.
        if (typeof container === "string") {
            return document.querySelector(container);
        }

        // Reuse a real element directly.
        if (container instanceof Element) {
            return container;
        }

        return null;

    }, []);

    // Push the resolved portal theme colours into the product module mount root.
    const applyPortalThemeToContainer = useCallback((container) => {
        const containerElement = resolveContainerElement(container);

        if (!containerElement) {
            return;
        }

        Object.entries(PRODUCT_THEME_VARIABLE_MAP).forEach(([themeKey, cssVariable]) => {
            containerElement.style.setProperty(cssVariable, themeColours[themeKey]);
        });
    }, [resolveContainerElement, themeColours]);

    // Render the full product page into a container through the shared product module.
    const renderProductPage = useCallback((container, options = {}) => {

        // Stop here if the hook is disabled for this render path.
        if (!enabled) {
            throw new Error("Product module is disabled");
        }

        // Stop here if the script is not ready yet.
        if (status !== "ready") {
            throw new Error("Product module script not ready");
        }

        // Stop here if the full product page renderer is missing on the page.
        if (typeof window.strich?.product?.productPage !== "function") {
            throw new Error("Product page renderer not available on window");
        }

        // Keep the rendered module on the same portal theme tokens as the shared cart hook.
        applyPortalThemeToContainer(container);

        // Let the shared module render the full product page.
        return window.strich.product.productPage(container, {
            ...options,
            cartSettings: options.cartSettings || productCartSettings,
        });

    }, [applyPortalThemeToContainer, enabled, productCartSettings, status]);

    // Render the compact product card into a container through the shared product module.
    const renderProductCard = useCallback((container, options = {}) => {

        // Stop here if the hook is disabled for this render path.
        if (!enabled) {
            throw new Error("Product module is disabled");
        }

        // Stop here if the script is not ready yet.
        if (status !== "ready") {
            throw new Error("Product module script not ready");
        }

        // Stop here if the compact product card renderer is missing on the page.
        if (typeof window.strich?.product?.productCard !== "function") {
            throw new Error("Product card renderer not available on window");
        }

        // Keep the rendered module on the same portal theme tokens as the shared cart hook.
        applyPortalThemeToContainer(container);

        // Let the shared module render the compact product card.
        return window.strich.product.productCard(container, {
            ...options,
            cartSettings: options.cartSettings || productCartSettings,
        });

    }, [applyPortalThemeToContainer, enabled, productCartSettings, status]);

    // Expose the shared product helpers and simple ready state flags.
    return {

        // The shared product API on window when it is available.
        productModule,

        // Main way to render a full product page through the shared module.
        renderProductPage,

        // Main way to render a compact product card through the shared module.
        renderProductCard,

        // True when the script has finished loading.
        isReady: status === "ready",

        // True while the script is still loading.
        isLoading: status === "loading",

        // True when the script failed to load.
        error: status === "error",

        // Expose the raw status as well.
        status,

    };

}
